export const BOARD_OPTIONS = ["CBSE", "ICSE", "STATE", "IGCSE"];

export const GRADE_OPTIONS = [
  "Class I-V Tuition",
  "Class VI-VIII Tuition",
  "Class IX-X Tuition",
  "Class XI-XII Tuition",
];

export const GRADE_LABELS = {
  "Class I-V Tuition": "Class I – V",
  "Class VI-VIII Tuition": "Class VI – VIII",
  "Class IX-X Tuition": "Class IX – X",
  "Class XI-XII Tuition": "Class XI – XII",
};

export const BOARD_LABELS = {
  CBSE: "CBSE",
  ICSE: "ICSE",
  STATE: "State Board",
  IGCSE: "IGCSE",
};

export const parseSubjectCategoryList = (subjectCategoryList = []) => {
  const boards = new Set();
  const grades = new Set();
  const categoryIds = [];

  subjectCategoryList.forEach((item) => {
    if (item?.categoryId) {
      categoryIds.push(item.categoryId);
    }
    if (item?.grade?.board) {
      boards.add(item.grade.board);
    }
    if (item?.grade?.gradeName) {
      grades.add(item.grade.gradeName);
    }
  });

  return {
    boards: [...boards],
    grades: [...grades],
    categoryIds,
  };
};

export const resolveStudyGroupType = ({ oneOnOne, group }) => {
  if (oneOnOne && group) return "BOTH";
  if (group) return "GROUP";
  if (oneOnOne) return "ONE_ON_ONE";
  return "";
};

/** Normalize API / form values to ONE_ON_ONE | GROUP | BOTH. */
export const normalizeStudyGroupTypeValue = (value) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim().toUpperCase();
  if (raw === "ONE_ON_ONE" || raw === "GROUP" || raw === "BOTH") {
    return raw;
  }
  if (raw === "INDIVIDUAL") return "ONE_ON_ONE";
  return "";
};

export const parseStudyGroupType = (studyGroupType = "") => {
  const normalized = normalizeStudyGroupTypeValue(studyGroupType);
  return {
    oneOnOne: normalized === "ONE_ON_ONE" || normalized === "BOTH",
    group: normalized === "GROUP" || normalized === "BOTH",
  };
};

export const studyGroupTypesMatch = (expected, actual) =>
  normalizeStudyGroupTypeValue(expected) === normalizeStudyGroupTypeValue(actual);

export const filterCategoriesForSelection = (
  subjectData,
  selectedBoards,
  selectedGrades,
) => {
  if (!selectedBoards.length || !selectedGrades.length) {
    return [];
  }

  return subjectData.filter(
    (item) =>
      selectedBoards.includes(item.grade?.board) &&
      selectedGrades.includes(item.grade?.gradeName),
  );
};

/** One row per subject name; each row maps to all matching category IDs. */
export const groupCategoriesBySubjectName = (categories = []) => {
  const map = new Map();

  categories.forEach((item) => {
    const subjectName = item.subject?.subjectName?.trim() || "Unknown";
    const key = subjectName.toLowerCase();

    if (!map.has(key)) {
      map.set(key, { subjectName, categoryIds: [] });
    }
    map.get(key).categoryIds.push(item.categoryId);
  });

  return [...map.values()].sort((a, b) =>
    a.subjectName.localeCompare(b.subjectName),
  );
};

export const isSubjectGroupFullySelected = (categoryIds, selectedCategoryIds) =>
  categoryIds.length > 0 &&
  categoryIds.every((id) => selectedCategoryIds.includes(id));

export const isSubjectGroupSelected = (categoryIds, selectedCategoryIds) =>
  categoryIds.some((id) => selectedCategoryIds.includes(id));

export const toggleSubjectGroupSelection = (
  categoryIds,
  checked,
  selectedCategoryIds,
) => {
  if (checked) {
    return [...new Set([...selectedCategoryIds, ...categoryIds])];
  }
  const remove = new Set(categoryIds);
  return selectedCategoryIds.filter((id) => !remove.has(id));
};

export const buildSubjectCategoryPayload = (categoryIds = []) =>
  categoryIds.map((categoryId) => ({ categoryId }));

/** Keep subject selections in sync when boards/grades change (multi-board × multi-grade). */
export const syncCategoryIdsForTeachingSelection = (
  subjectData,
  selectedBoards,
  selectedGrades,
  selectedCategoryIds = [],
) => {
  if (!subjectData.length || !selectedBoards.length || !selectedGrades.length) {
    return selectedCategoryIds;
  }

  const allowed = filterCategoriesForSelection(
    subjectData,
    selectedBoards,
    selectedGrades,
  );
  const allowedIdSet = new Set(allowed.map((item) => item.categoryId));

  const selectedSubjectKeys = new Set();
  subjectData.forEach((item) => {
    if (selectedCategoryIds.includes(item.categoryId)) {
      const key = item.subject?.subjectName?.trim().toLowerCase() || "";
      if (key) {
        selectedSubjectKeys.add(key);
      }
    }
  });

  if (selectedSubjectKeys.size === 0) {
    return selectedCategoryIds.filter((id) => allowedIdSet.has(id));
  }

  const synced = new Set();
  allowed.forEach((item) => {
    const key = item.subject?.subjectName?.trim().toLowerCase() || "";
    if (selectedSubjectKeys.has(key)) {
      synced.add(item.categoryId);
    }
  });

  return [...synced];
};

export const normalizeDegreeStatus = (value) => {
  if (value == null || value === "") return "";
  const normalized = String(
    typeof value === "object" && value !== null ? value.name ?? value.value : value,
  )
    .trim()
    .toUpperCase();
  if (normalized === "COMPLETED" || normalized === "PURSUING" || normalized === "DROP") {
    return normalized;
  }
  return "";
};

export const DEGREE_OPTIONS = ["Diploma", "Bachelor's", "Master's"];

const DEGREE_CANONICAL = {
  M: "Master's",
  B: "Bachelor's",
  D: "Diploma",
};

/** Read degree from GET profile payloads (camelCase or snake_case). */
export const getEducationalBackgroundFromProfile = (data = {}) =>
  data.educationalBackground ??
  data.educational_background ??
  data.degree ??
  data.degreeName ??
  "";

/** Map API / DB degree strings to select option values. */
export const normalizeEducationalBackground = (value) => {
  if (value == null || value === "") return "";

  const raw = String(value).trim();
  const withStraightApostrophe = raw.replace(/[\u2018\u2019\u2032`]/g, "'");

  const exact = DEGREE_OPTIONS.find(
    (option) => option.toLowerCase() === withStraightApostrophe.toLowerCase(),
  );
  if (exact) return exact;

  const upper = withStraightApostrophe.toUpperCase();
  if (DEGREE_CANONICAL[upper]) return DEGREE_CANONICAL[upper];
  if (/^MASTER/.test(upper)) return DEGREE_OPTIONS[2];
  if (/^BACHELOR/.test(upper)) return DEGREE_OPTIONS[1];
  if (/^DIPLOMA/.test(upper)) return DEGREE_OPTIONS[0];

  return withStraightApostrophe;
};

export const unwrapTutorProfileResponse = (payload) => {
  if (!payload || typeof payload !== "object") return {};
  if (payload.tutorId || payload.user || payload.educationalBackground != null) {
    return payload;
  }
  if (payload.data && typeof payload.data === "object") {
    return payload.data;
  }
  return payload;
};

export const mapTutorProfessionalFields = (data = {}) => ({
  educationalBackground: normalizeEducationalBackground(
    getEducationalBackgroundFromProfile(data),
  ),
  degreeStatus: normalizeDegreeStatus(data.degreeStatus),
  yearofcompletion: data.yearofcompletion
    ? new Date(data.yearofcompletion).toISOString().split("T")[0]
    : "",
  upiId: data.upiId || "",
  accountHolderName: data.accountHolderName || "",
});

export const normalizeIndividualFlag = (value) =>
  value === true || value === "true";

export const normalizeLanguagesMap = (languages = {}) => {
  const normalized = {};
  Object.entries(languages || {}).forEach(([language, proficiency]) => {
    const lang = String(language).trim().toUpperCase();
    const prof = String(proficiency).trim().toUpperCase();
    if (lang && prof) {
      normalized[lang] = prof;
    }
  });
  return normalized;
};

/** PUT /tutor/profile — only fields TutorDTO accepts (no GET response extras). */
export const buildTutorProfileUpdatePayload = ({
  proffData,
  tutorId,
  selectedCategoryIds = [],
  selectedBoards = [],
  selectedGrades = [],
  subjectData = [],
  studyGroupTypes,
  tabType = "tutorProfessionalInfo",
}) => {
  const individual = normalizeIndividualFlag(proffData.individual);
  const degreeStatus = normalizeDegreeStatus(proffData.degreeStatus);
  const studyGroupType = resolveStudyGroupType(studyGroupTypes);
  const board = selectedBoards[0] || proffData.board || undefined;
  const yearOfExp = parseInt(proffData.yearOfExp, 10);
  const feePerHour = parseFloat(proffData.feePerHour);
  const syncedCategoryIds = syncCategoryIdsForTeachingSelection(
    subjectData,
    selectedBoards,
    selectedGrades,
    selectedCategoryIds,
  );

  const timeslots = (Array.isArray(proffData.timeslots) ? proffData.timeslots : [])
    .map((slot) => ({
      timeSlotId: slot.timeSlotId,
      daysOfWeek: slot.daysOfWeek,
    }))
    .filter((slot) => slot.timeSlotId && slot.daysOfWeek);

  const payload = {
    tutorId,
    tabType,
    individual,
    languages: normalizeLanguagesMap(proffData.languages),
    yearOfExp: Number.isFinite(yearOfExp) ? yearOfExp : 0,
    experience: proffData.experience,
    feePerHour: Number.isFinite(feePerHour) ? feePerHour : 0,
    upiId: (proffData.upiId || "").trim(),
    accountHolderName: (proffData.accountHolderName || "").trim(),
    gradeCategory: proffData.gradeCategory || "PRIMARY",
    tuitionMode: "ONLINE",
    subjectCategoryList: buildSubjectCategoryPayload(syncedCategoryIds),
    timeslots,
  };

  const educationalBackground = normalizeEducationalBackground(
    proffData.educationalBackground,
  );
  if (educationalBackground) {
    payload.educationalBackground = educationalBackground;
  }

  if (proffData.yearofcompletion) {
    payload.yearofcompletion = proffData.yearofcompletion;
  }

  if (board) {
    payload.board = board;
  }

  if (studyGroupType) {
    payload.studyGroupType = studyGroupType;
  }

  if (degreeStatus) {
    payload.degreeStatus = degreeStatus;
  }

  if (!individual && proffData.institution?.institutionId) {
    payload.institution = {
      institutionId: proffData.institution.institutionId,
    };
  }

  return payload;
};
export const buildTutorProfileSubmitPayload = ({
  formData,
  selectedCategoryIds = [],
  selectedBoards = [],
  selectedGrades = [],
  subjectData = [],
  studyGroupTypes,
  overrides = {},
}) => {
  const individual = normalizeIndividualFlag(
    overrides.individual ?? formData.individual,
  );
  const degreeStatus = normalizeDegreeStatus(formData.degreeStatus);
  const studyGroupType = resolveStudyGroupType(studyGroupTypes);
  const board = selectedBoards[0] || formData.board || undefined;
  const yearOfExp = parseInt(formData.yearOfExp, 10);
  const feePerHour = parseFloat(formData.feePerHour);
  const syncedCategoryIds = syncCategoryIdsForTeachingSelection(
    subjectData,
    selectedBoards,
    selectedGrades,
    selectedCategoryIds,
  );

  const payload = {
    user: formData.user,
    languages: normalizeLanguagesMap(formData.languages || {}),
    educationalBackground: normalizeEducationalBackground(
      formData.educationalBackground,
    ),
    yearOfExp: Number.isFinite(yearOfExp) ? yearOfExp : 0,
    experience: formData.experience,
    feePerHour: Number.isFinite(feePerHour) ? feePerHour : 0,
    upiId: (formData.upiId || "").trim(),
    accountHolderName: (formData.accountHolderName || "").trim(),
    gradeCategory: formData.gradeCategory || "PRIMARY",
    tuitionMode: "ONLINE",
    individual,
    subjectCategoryList: buildSubjectCategoryPayload(syncedCategoryIds),
    timeslots: Array.isArray(formData.timeslots) ? formData.timeslots : [],
  };

  if (formData.yearofcompletion) {
    payload.yearofcompletion = formData.yearofcompletion;
  }

  if (board) {
    payload.board = board;
  }

  if (studyGroupType) {
    payload.studyGroupType = studyGroupType;
  }

  if (degreeStatus) {
    payload.degreeStatus = degreeStatus;
  }

  if (!individual) {
    if (overrides.institution?.institutionId) {
      payload.institution = overrides.institution;
    } else if (formData.institution?.institutionId) {
      payload.institution = formData.institution;
    }
  }

  return payload;
};

export const primaryBoardFromCategories = (subjectCategoryList = []) => {
  const first = subjectCategoryList.find((item) => item?.grade?.board);
  return first?.grade?.board || "";
};

export const MANDATORY_TUTOR_DOCUMENT_TYPES = ["kyc1", "cert1"];

export const MANDATORY_DOCUMENT_MESSAGE =
  "Please upload Photo ID Card and Educational Certificates.";

const isPhotoIdDocType = (docType = "") =>
  docType === "kyc1" ||
  docType === "KYC" ||
  docType.toLowerCase().startsWith("kyc");

const isEducationalCertDocType = (docType = "") =>
  docType === "cert1" || docType === "Certification";

export const countProfileDocuments = (documents = []) =>
  (documents || []).filter((doc) => !doc?.profileImage).length;

export const validateMandatoryTutorDocuments = ({
  existingDocuments = [],
  pendingFields = [],
  pendingUploadCount = 0,
}) => {
  const totalDocuments =
    countProfileDocuments(existingDocuments) + pendingUploadCount;

  if (totalDocuments >= MANDATORY_TUTOR_DOCUMENT_TYPES.length) {
    return { isValid: true, message: "" };
  }

  const hasPhotoId = pendingFields.some(
    (field) => field.file && isPhotoIdDocType(field.docType),
  );
  const hasEducationalCert = pendingFields.some(
    (field) => field.file && isEducationalCertDocType(field.docType),
  );

  if (!hasPhotoId || !hasEducationalCert) {
    return {
      isValid: false,
      message: MANDATORY_DOCUMENT_MESSAGE,
    };
  }

  return { isValid: true, message: "" };
};
